import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../lib/api';
import { formatNumber, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  RefreshCw,
  BarChart3,
  Download,
  FileText,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { toast } from 'sonner';

const RISK_COLORS = {
  critical: '#FF003D',
  high: '#FF6B00',
  medium: '#FF9900',
  low: '#00FF94',
};

const STATUS_COLORS = {
  new: '#00F0FF',
  investigating: '#FF9900',
  escalated: '#FF003D',
  resolved: '#00FF94',
  false_positive: '#64748B',
};

export default function Reports() {
  const [entityRiskData, setEntityRiskData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [alertData, setAlertData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportPeriod, setReportPeriod] = useState('30');

  useEffect(() => {
    fetchReportData();
  }, [reportPeriod]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const [entityRes, txnRes, alertRes] = await Promise.all([
        reportsAPI.entityRiskSummary(),
        reportsAPI.transactionSummary(parseInt(reportPeriod)),
        reportsAPI.alertMetrics(),
      ]);

      // Transform entity risk data
      const entityTransformed = entityRes.data.map((item) => ({
        entityType: item._id.entity_type || 'Unknown',
        riskLevel: item._id.risk_level || 'low',
        count: item.count,
        fill: RISK_COLORS[item._id.risk_level] || RISK_COLORS.low,
      }));
      setEntityRiskData(entityTransformed);

      // Transform transaction data
      const txnTransformed = txnRes.data.map((item) => ({
        type: item._id || 'Unknown',
        total: item.count,
        flagged: item.flagged_count,
        amount: item.total_amount,
      }));
      setTransactionData(txnTransformed);

      // Transform alert data
      const alertTransformed = alertRes.data.map((item) => ({
        status: item._id.status || 'unknown',
        severity: item._id.severity || 'low',
        count: item.count,
        fill: STATUS_COLORS[item._id.status] || '#64748B',
      }));
      setAlertData(alertTransformed);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (reportType) => {
    toast.success(`${reportType} report exported`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Reports & Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Intelligence summaries and regulatory reports
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={reportPeriod} onValueChange={setReportPeriod}>
            <SelectTrigger className="w-[150px] bg-black/20 border-white/10" data-testid="report-period-select">
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => fetchReportData()} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      ) : (
        <>
          {/* Transaction Analysis */}
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4 flex flex-row items-center justify-between">
              <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
                Transaction Analysis by Type
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleExport('Transaction')}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-80">
                {transactionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={transactionData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis
                        dataKey="type"
                        stroke="#64748B"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="#64748B"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(222, 47%, 11%)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}
                      />
                      <Legend />
                      <Bar dataKey="total" fill="#00F0FF" name="Total" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="flagged" fill="#FF003D" name="Flagged" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <p className="text-muted-foreground">No transaction data available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Entity Risk Distribution */}
            <Card className="glass">
              <CardHeader className="border-b border-white/5 pb-4 flex flex-row items-center justify-between">
                <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
                  Entity Risk Distribution
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleExport('Entity Risk')}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="p-4">
                <div className="h-64">
                  {entityRiskData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={entityRiskData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="riskLevel"
                        >
                          {entityRiskData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(222, 47%, 11%)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '4px',
                            fontSize: '12px',
                          }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-muted-foreground">No entity data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Alert Metrics */}
            <Card className="glass">
              <CardHeader className="border-b border-white/5 pb-4 flex flex-row items-center justify-between">
                <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
                  Alert Status Distribution
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleExport('Alert Metrics')}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="p-4">
                <div className="h-64">
                  {alertData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={alertData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="status"
                        >
                          {alertData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(222, 47%, 11%)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '4px',
                            fontSize: '12px',
                          }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-muted-foreground">No alert data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Reports */}
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <CardTitle className="font-secondary text-sm font-semibold uppercase tracking-wider text-white">
                Generate Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start gap-2"
                  onClick={() => handleExport('SAR Summary')}
                  data-testid="generate-sar-report"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="font-secondary uppercase tracking-wider">SAR Summary</span>
                  </div>
                  <p className="text-xs text-muted-foreground text-left">
                    Generate suspicious activity report summary
                  </p>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start gap-2"
                  onClick={() => handleExport('Risk Assessment')}
                  data-testid="generate-risk-report"
                >
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    <span className="font-secondary uppercase tracking-wider">Risk Assessment</span>
                  </div>
                  <p className="text-xs text-muted-foreground text-left">
                    Comprehensive entity risk analysis
                  </p>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start gap-2"
                  onClick={() => handleExport('FATF Compliance')}
                  data-testid="generate-fatf-report"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="font-secondary uppercase tracking-wider">FATF Compliance</span>
                  </div>
                  <p className="text-xs text-muted-foreground text-left">
                    FATF recommendations compliance report
                  </p>
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
